import { useState, useRef, useEffect } from "react";
import "../Css/CoursePage.css";

// ── Slug helpers ─────────────────────────────────────────────────────────────
function fromSlug(slug = "") {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function getStreamKey(slug = "") {
  if (slug.includes("jee-main-advanced") || slug.includes("jee-advanced")) return "jee-advanced";
  if (slug.includes("jee-main")) return "jee-main";
  if (slug.includes("neet")) return "neet";
  if (slug.includes("grade") || slug.includes("6") || slug.includes("pncf") || slug.includes("foundation")) return "grade";
  return "jee-advanced";
}

// ── City list (mirrors ALLEN center dropdown) ─────────────────────────────────
const CITIES = [
  "Agra","Ahmedabad","Akola","Alwar","Amritsar","Bathinda","Bengaluru","Bhopal",
  "Bhubaneswar","Chandigarh","Chennai","Dehradun","Delhi","Faridabad","Guwahati",
  "Gwalior","Hyderabad","Indore","Jaipur","Jalandhar","Jammu","Jodhpur","Kanpur",
  "Kochi","Kota","Lucknow","Ludhiana","Mumbai","Nagpur","Noida","Patna","Pune",
  "Raipur","Rajkot","Ranchi","Surat","Vadodara","Varanasi","Vijayawada","Visakhapatnam",
];

// ── Stream options ─────────────────────────────────────────────────────────────
const STREAMS = [
  { key: "jee-advanced", label: "JEE Main Advanced" },
  { key: "jee-main",     label: "JEE Main" },
  { key: "neet",         label: "NEET" },
  { key: "grade",        label: "Grade 6–10" },
];

// ── Sessions ──────────────────────────────────────────────────────────────────
const SESSIONS = ["2026–27", "2025–26"];

// ── Classes ───────────────────────────────────────────────────────────────────
const CLASSES = ["Class VIII", "Class IX", "Class X", "Class XI", "Class XII", "Dropper"];

// ══════════════════════════════════════════════════════════════
// DATA
// ══════════════════════════════════════════════════════════════
const DATA = {
  "jee-advanced": {
    label: "JEE Main Advanced",
    pageTitle: (city) => `IIT JEE Coaching in ${city}`,
    streamTag: "JEE (MAIN+ADVANCED)",
    courses: [
      { tag: "TARGET 2026", tagClass: "tag-blue", name: "NURTURE",    desc: "Class X to XI Moving Students (Duration: 2 Years)" },
      { tag: "TARGET 2026", tagClass: "tag-blue", name: "OPENPRO XI", desc: "Class X to XI Moving Students (Duration: 2 Years)" },
      { tag: "TARGET 2027", tagClass: "tag-amber",name: "ENTHUSIAST", desc: "Class XI to XII Moving Students (Duration: 1 Year)" },
      { tag: "TARGET 2027", tagClass: "tag-amber",name: "LEADER",     desc: "Class XII Passed Students (Duration: 1 Year)" },
    ],
    about: {
      heading: (city) => `IIT JEE Coaching in ${city} by IITian's Chinmay Academy`,
      body: (city) => `IITian's Chinmay Academy, India's most trusted name in engineering entrance coaching, now offers result-driven JEE Main + Advanced Coaching in ${city}, designed to help students secure admission to the prestigious IITs, NITs, and other top engineering colleges across the country.\n\nFor decades, the academy has consistently produced top JEE ranks through its structured classroom programs, expert faculty team, and student-centered academic system. With a proven legacy of results, it ensures that every aspirant receives the guidance, mentoring, and practice needed to achieve excellence in one of India's toughest entrance exams.`,
    },
    sec1: {
      title: (city) => `Engineering (JEE Main + Advanced) Classroom Courses Offered in ${city}`,
      intro: (city) => `The academy offers a range of meticulously designed JEE classroom programs at its ${city} center, such as NURTURE, OPENPRO XI, and ENTHUSIAST, each tailored to different academic stages. These courses integrate concept clarity, analytical problem-solving, and regular testing, ensuring students receive targeted and effective preparation.`,
      table: [
        { course: "NURTURE",    who: "Students moving from Class X to XI",   dur: "2 Years" },
        { course: "OPENPRO XI", who: "Students moving from Class X to XI",   dur: "2 Years" },
        { course: "ENTHUSIAST", who: "Students moving from Class XI to XII",  dur: "1 Year" },
      ],
      outro: "These programs provide a comprehensive curriculum, starting from building strong fundamentals to mastering advanced-level concepts essential for securing top ranks in both JEE Main and Advanced. Students also receive regular performance analysis, personalised doubt-solving sessions, and focused mentoring to help them stay consistent throughout their preparation journey.",
    },
    sec2: {
      title: (city) => `Why IITian's Chinmay Academy is the Most Trusted JEE Coaching in ${city} for Engineering Aspirants`,
      intro: (city) => `In today's competitive environment, JEE aspirants in ${city} face the dual challenge of maintaining an extensive syllabus and maintaining consistent motivation. The academy helps students overcome these hurdles through a systematic academic structure and a personalised learning approach.`,
      points: [
        { h: "Structured & Result-Oriented Study Plan", b: "The academy provides a well-defined study roadmap, designed and supervised by IIT-qualified experts. Students follow a consistent schedule that ensures the complete syllabus is covered efficiently, with adequate time for revision and test practice." },
        { h: "Expert Faculty & Focused Mentoring", b: "The faculty team comprises highly experienced and subject-specialist mentors who have guided numerous top JEE rankers. They emphasise conceptual clarity, problem-solving techniques, and exam-oriented learning strategies." },
        { h: "Consistent Evaluation & Test Series", b: "Regular tests based on the actual JEE Main and Advanced pattern help students identify their strengths and areas for improvement. Each test is followed by performance analysis, enabling students to track progress and refine their preparation." },
        { h: "Conceptual Clarity & Application-Based Learning", b: "Engineering exams demand not just knowledge but application. Teaching methodology focuses on building logical reasoning, critical thinking, and numerical accuracy — skills essential for high JEE scores." },
        { h: "Motivation, Mentorship & Emotional Support", b: "Mentors act as both guides and motivators. Regular counselling sessions and one-on-one doubt-solving help students stay confident, especially during challenging phases of preparation." },
      ],
    },
    sec3: {
      title: "Key Features of IITian's Chinmay Academy JEE (Main + Advanced) Coaching",
      items: [
        { h: "Experienced Faculty Members", b: "A team of subject experts with years of experience in JEE coaching provides precise guidance, conceptual clarity, and a performance-driven approach across Physics, Chemistry, and Mathematics." },
        { h: "Comprehensive Study Material", b: "The study material covers topic-wise theory, NCERT-based fundamentals, problem sets of varying difficulty levels, and previous years' question papers." },
        { h: "Regular Testing & Performance Tracking", b: "Frequent online and offline tests simulate the actual exam pattern. Students receive detailed reports highlighting accuracy, time management, and topic-wise performance." },
        { h: "Personalised Mentorship & Support", b: "Each student receives personalised guidance through regular doubt-clearing sessions, mentor feedback, and academic counselling." },
        { h: "Updated JEE Syllabus & Exam Pattern Coverage", b: "The academy continuously updates its course content and teaching methods based on the latest JEE trends." },
        { h: "Disciplined and Competitive Classroom Environment", b: "A focused classroom atmosphere motivates students to stay consistent and competitive." },
        { h: "Dedicated Doubt Resolution Sessions", b: "Subject experts are available to resolve doubts instantly, ensuring no student leaves confused." },
      ],
    },
    sec4: {
      title: (city) => `IIT JEE Study Material for Students in ${city}`,
      intro: () => `Boost your JEE preparation with expertly curated study materials. Download free PDFs covering Physics, Chemistry, and Mathematics — complete with mock tests, formulae, and practice questions.`,
      leftTitle: "JEE Main",
      left: ["JEE Main Previous Year Papers", "JEE Main Sample Papers", "JEE Main Mock Test", "JEE Main Syllabus", "JEE Main Topic-wise study material"],
      rightTitle: "JEE Advanced",
      right: ["JEE Advanced Previous Year Papers", "JEE Advanced Sample Papers", "JEE Advanced Mock Test", "JEE Advanced Syllabus", "JEE Advanced Chapter Wise Questions with Solutions"],
    },
    faq: {
      title: (city) => `FAQs: JEE Coaching in ${city}`,
      questions: [
        "Why is IITian's Chinmay Academy considered the best institute for JEE preparation?",
        "What JEE classroom programs are available?",
        "Does IITian's Chinmay Academy provide study material for JEE preparation?",
        "Are scholarships available for JEE aspirants?",
        "Does the academy follow the latest JEE syllabus and exam pattern?",
      ],
    },
  },

  "jee-main": {
    label: "JEE Main",
    pageTitle: (city) => `JEE Main Coaching in ${city}`,
    streamTag: "JEE (MAIN)",
    courses: [
      { tag: "TARGET 2026", tagClass: "tag-blue",  name: "NURTURE",    desc: "Class X to XI Moving Students (Duration: 2 Years)" },
      { tag: "TARGET 2026", tagClass: "tag-blue",  name: "ACHIEVER",   desc: "Class X to XI Moving Students (Duration: 2 Years)" },
      { tag: "TARGET 2027", tagClass: "tag-amber", name: "ENTHUSIAST", desc: "Class XI to XII Moving Students (Duration: 1 Year)" },
      { tag: "TARGET 2027", tagClass: "tag-amber", name: "DROPPER",    desc: "Class XII Passed Students (Duration: 1 Year)" },
    ],
    about: {
      heading: (city) => `JEE Main Coaching in ${city} by IITian's Chinmay Academy`,
      body: (city) => `IITian's Chinmay Academy offers focused JEE Main Coaching in ${city}, designed to help students secure admission to the top NITs, IIITs, and GFTIs across India. With an expert faculty team and structured classroom programs, the academy ensures each aspirant is fully prepared for JEE Main.`,
    },
    sec1: {
      title: (city) => `JEE Main Classroom Courses Offered in ${city}`,
      intro: (city) => `The academy offers a range of JEE Main classroom programs at its ${city} center, tailored to different academic stages.`,
      table: [
        { course: "NURTURE",    who: "Students moving from Class X to XI",  dur: "2 Years" },
        { course: "ACHIEVER",   who: "Students moving from Class X to XI",  dur: "2 Years" },
        { course: "ENTHUSIAST", who: "Students moving from Class XI to XII", dur: "1 Year" },
      ],
      outro: "These programs provide a comprehensive curriculum to help students master all concepts required for JEE Main.",
    },
    sec2: {
      title: (city) => `Why IITian's Chinmay Academy is the Most Trusted JEE Main Coaching in ${city}`,
      intro: (city) => `Students in ${city} rely on IITian's Chinmay Academy for its structured approach, expert faculty, and consistent results.`,
      points: [
        { h: "Structured & Result-Oriented Study Plan", b: "A well-defined study roadmap supervised by IIT-qualified experts ensures complete syllabus coverage." },
        { h: "Expert Faculty & Focused Mentoring", b: "Subject-specialist mentors emphasise conceptual clarity, problem-solving, and exam-oriented learning." },
        { h: "Consistent Evaluation & Test Series", b: "Regular JEE Main pattern tests help identify strengths and areas for improvement." },
        { h: "Conceptual Clarity & Application-Based Learning", b: "Teaching methodology focuses on building logical reasoning, critical thinking, and numerical accuracy." },
        { h: "Motivation, Mentorship & Emotional Support", b: "Regular counselling sessions and one-on-one doubt-solving keep students confident throughout preparation." },
      ],
    },
    sec3: {
      title: "Key Features of JEE Main Coaching",
      items: [
        { h: "Experienced Faculty Members", b: "Subject experts with years of JEE coaching experience provide precise guidance and conceptual clarity." },
        { h: "Comprehensive Study Material", b: "Topic-wise theory, NCERT fundamentals, problem sets, and previous years' papers ensure thorough preparation." },
        { h: "Regular Testing & Performance Tracking", b: "Frequent tests with detailed reports help track accuracy, time management, and topic-wise performance." },
        { h: "Personalised Mentorship & Support", b: "Regular doubt-clearing sessions and academic counselling help overcome weaknesses and enhance strengths." },
        { h: "Updated JEE Syllabus & Exam Pattern Coverage", b: "Course content and teaching methods are continuously updated based on the latest JEE trends." },
        { h: "Disciplined and Competitive Classroom Environment", b: "A focused atmosphere motivates students to stay consistent and competitive." },
        { h: "Dedicated Doubt Resolution Sessions", b: "Subject experts resolve doubts instantly, ensuring concept retention and exam confidence." },
      ],
    },
    sec4: {
      title: (city) => `JEE Main Study Material for Students in ${city}`,
      intro: () => `Download free PDFs covering Physics, Chemistry, and Mathematics for JEE Main preparation.`,
      leftTitle: "",
      left: ["JEE Main Previous Year Papers", "JEE Main Sample Papers", "JEE Main Mock Test", "JEE Main Syllabus", "JEE Main Topic-wise study material"],
      rightTitle: "",
      right: [],
    },
    faq: {
      title: (city) => `FAQs: JEE Main Coaching in ${city}`,
      questions: [
        "Why is IITian's Chinmay Academy the best choice for JEE Main preparation?",
        "What JEE Main classroom programs are available?",
        "Does the academy provide study material for JEE Main?",
        "Are scholarships available for JEE Main aspirants?",
        "Does the academy follow the latest JEE Main syllabus?",
      ],
    },
  },

  "neet": {
    label: "NEET",
    pageTitle: (city) => `NEET(UG) Coaching in ${city}`,
    streamTag: "PRE-MEDICAL (NEET-UG)",
    courses: [
      { tag: "TARGET 2026", tagClass: "tag-blue",  name: "NURTURE",    desc: "Class X to XI Moving Students (Duration: 2 Years)" },
      { tag: "TARGET 2026", tagClass: "tag-blue",  name: "OPENPRO XI", desc: "Class X to XI Moving Students (Duration: 2 Years)" },
      { tag: "TARGET 2027", tagClass: "tag-amber", name: "ENTHUSIAST", desc: "Class XI to XII Moving Students (Duration: 1 Year)" },
      { tag: "TARGET 2027", tagClass: "tag-amber", name: "LEADER",     desc: "Class XII Passed Students (Duration: 1 Year)" },
    ],
    about: {
      heading: (city) => `NEET (UG) Coaching in ${city} by IITian's Chinmay Academy`,
      body: (city) => `Preparing for NEET UG requires strong fundamentals, expert guidance, and the right learning environment. IITian's Chinmay Academy is a trusted name in medical entrance coaching, now offers result-oriented NEET Coaching in ${city} to help students achieve top ranks and secure admission to leading MBBS and BDS colleges across India.\n\nThe academy provides structured classroom programs, experienced faculty, and high-quality study resources tailored to the latest NEET exam pattern.`,
    },
    sec1: {
      title: (city) => `PRE-MEDICAL (NEET-UG) Classroom Courses Offered in ${city}`,
      intro: () => "",
      table: [
        { course: "NURTURE",    who: "Class X to XI Moving Students",  dur: "2 Years" },
        { course: "OPENPRO XI", who: "Class X to XI Moving Students",  dur: "2 Years" },
        { course: "ENTHUSIAST", who: "Class XI to XII Moving Students", dur: "1 Year" },
      ],
      outro: "",
    },
    sec2: {
      title: (city) => `Why IITian's Chinmay Academy is the Most Trusted NEET Coaching in ${city}?`,
      intro: (city) => `The ${city} NEET coaching has growing competition for medical entrance exams, and many students struggle with self-preparation due to the volume of the syllabus and limited practice. High-quality NEET coaching helps aspirants by:`,
      points: [
        { h: "Providing a Clear Study Path", b: "Students get a structured timetable, expert-curated material, and guidance on what to study and when." },
        { h: "Improving Concept Clarity", b: "NEET needs strong fundamentals in Physics, Chemistry, and Biology. The teaching methodology ensures conceptual strength, not just memorisation." },
        { h: "Offering Regular Tests & Practice", b: "Frequent tests based on NEET patterns help students understand exam trends, improve accuracy, and build speed." },
        { h: "Keeping Students Motivated & Consistent", b: "Personalised mentorship ensures each student feels supported and motivated, especially when exam pressure rises." },
      ],
    },
    sec3: {
      title: "Key Features of IITian's Chinmay Academy NEET (UG) Coaching",
      items: [
        { h: "Experienced & Expert Faculty", b: "Highly qualified faculty members bring years of experience teaching competitive exams, helping students master every concept." },
        { h: "Comprehensive Study Material", b: "Topic-wise theory, NCERT coverage, practice questions, and previous-year patterns ensure complete preparation." },
        { h: "Regular NEET Pattern Tests (offline & online)", b: "Detailed performance analytics and error analysis reports help students improve continuously." },
        { h: "Personalised Mentorship & Doubt Solving", b: "Dedicated mentors support students academically and emotionally, keeping them motivated throughout the journey." },
        { h: "Disciplined Classroom Environment", b: "A focused, competitive environment helps students stay consistent, attentive, and exam-ready." },
        { h: "Dedicated Doubt Counters", b: "Subject experts are available to clear doubts instantly for better clarity and stronger fundamentals." },
      ],
    },
    sec4: {
      title: () => "NEET Study Material: Download Free PDFs",
      intro: () => `IITian's Chinmay Academy provides free NEET study material PDFs covering NCERT concepts, practice questions, mock tests, and previous-year papers.`,
      leftTitle: "",
      left: ["NEET Mock Test", "NEET Previous Year Papers", "NEET Sample Papers", "NEET Answer Key and Solutions", "NEET NCERT Books", "NEET Books"],
      rightTitle: "",
      right: [],
    },
    faq: {
      title: () => "Frequently Asked Questions (FAQs)",
      questions: [
        "Why is IITian's Chinmay Academy the Best Choice for NEET Coaching?",
        "What types of NEET classroom programs does the academy offer?",
        "Does the academy provide study material for NEET preparation?",
        "Does the academy offer scholarships for NEET coaching?",
        "Does the academy cover the latest NEET syllabus and exam pattern?",
        "How can I enrol in NEET Classroom Coaching?",
      ],
    },
    showLeadForm: true,
  },

  "grade": {
    label: "Grade 6–10",
    pageTitle: (city) => `Classes 6 to 12 Foundation Courses in ${city}`,
    streamTag: "PRE-NURTURE & CAREER FOUNDATION",
    courses: [
      { tag: "CLASSROOM", tagClass: "tag-green", name: "CLASS VIII", desc: "CLASS VII TO VIII MOVING STUDENTS (DURATION: 1 YEAR)" },
      { tag: "CLASSROOM", tagClass: "tag-green", name: "CLASS IX",   desc: "CLASS VIII TO IX MOVING STUDENTS (DURATION: 1 YEAR)" },
      { tag: "CLASSROOM", tagClass: "tag-green", name: "CLASS X",    desc: "CLASS IX TO X MOVING STUDENTS (DURATION: 1 YEAR)" },
    ],
    about: {
      heading: (city) => `Classes 6 to 12 Foundation Courses in ${city} by IITian's Chinmay Academy`,
      body: (city) => `IITian's Chinmay Academy offers its structured Pre-Nurture & Career Foundation (PNCF) programs for students of Classes 6 to 12 in ${city}. These programs aim to build strong academic fundamentals, logical thinking, and problem-solving skills at an early age, helping students excel in school exams as well as future competitive exams like Olympiads, NTSE, KVPY, JEE, and NEET.\n\nThrough expert faculty, concept-focused teaching, regular assessments, and specially curated study material, the academy ensures that young learners grow with clarity, confidence, and discipline.`,
    },
    sec1: {
      title: (city) => `Foundation Classroom Courses Offered in ${city} (Classes 6 to 12)`,
      intro: () => `The academy offers 1-year classroom programs for students transitioning to the next grade, focusing on conceptual development and holistic academic growth.`,
      tableWithFocus: true,
      table: [
        { course: "Class VIII", who: "Class VII → VIII Moving Students", dur: "1 Year", focus: "Strengthening school fundamentals, building reasoning skills, Introduction to competitive exam patterns" },
        { course: "Class IX",   who: "Class VIII → IX Moving Students",  dur: "1 Year", focus: "Advanced concept-building, Olympiad-oriented practice, Strong foundation for Class 10 & future competitive exams." },
        { course: "Class X",    who: "Class IX → X Moving Students",     dur: "1 Year", focus: "Board exam preparation, analytical problem-solving, early foundation for JEE/NEET pathways" },
      ],
      showFee: true,
      outro: "",
    },
    sec2: {
      title: () => "Why Choose IITian's Chinmay Academy for Foundation Courses (Classes 6–10)?",
      intro: () => "",
      points: [
        { h: "Expert Faculty for Strong Academic Foundations", b: "Students are guided by experienced faculty who specialise in teaching young learners and laying step-by-step concept clarity across subjects." },
        { h: "Competitive Edge from an Early Age", b: "The curriculum is designed to prepare students for Olympiads, NTSE, and other competitive exams, helping them develop analytical skills early." },
        { h: "Regular Tests and Performance Analysis", b: "Through periodic tests and detailed reports, students learn to identify areas of improvement and build exam discipline." },
        { h: "Structured Curriculum with NCERT Alignment", b: "Modules balance school syllabus requirements with competitive exam-oriented learning to ensure students stay ahead academically." },
        { h: "Positive, Motivating Classroom Environment", b: "Interactive sessions, peer learning, and mentoring help students stay confident and motivated throughout the year." },
      ],
    },
    sec3: {
      title: (city) => `Key Features of IITian's Chinmay Academy ${city} Foundation Programs`,
      items: [
        { h: "Comprehensive study material covering all major subjects", b: "" },
        { h: "Daily Practice Problems (DPPs) for consistency and clarity", b: "" },
        { h: "Weekly/Monthly tests modelled on Olympiad & school exam patterns", b: "" },
        { h: "Doubt-solving support for conceptual resolution", b: "" },
        { h: "Growth-oriented academic guidance and mentoring", b: "" },
        { h: "Development of reasoning, logic, and scientific temperament", b: "" },
      ],
    },
    sec3b: {
      title: "How Students Can Get the Most Out of Foundation Courses",
      items: [
        { h: "Attend classes consistently", b: "to maintain continuity and understanding." },
        { h: "Ask questions freely", b: "to strengthen conceptual clarity." },
        { h: "Practice daily using DPPs", b: "to build accuracy and confidence." },
        { h: "Revise regularly", b: "to reinforce topics learned in class." },
        { h: "Participate actively in tests", b: "to improve exam temperament." },
      ],
    },
    sec4: {
      title: () => "Foundation Study Material",
      intro: () => "Access comprehensive study material for Classes 6 to 12.",
      leftTitle: "",
      left: ["NTSE Study Material", "Olympiad Sample Papers", "NCERT Solutions", "Class 10 Board Practice Papers"],
      rightTitle: "",
      right: [],
    },
    faq: {
      title: (city) => `FAQs – Foundation Coaching (Classes 6–10) in ${city}`,
      questions: [
        "Which classes are covered under the foundation program?",
        "Is study material included in the fee?",
        "Do these courses help in Olympiad preparation?",
        "Are tests conducted regularly?",
        "Does this program help with future JEE/NEET preparation?",
      ],
    },
  },
};

// ── Feature list shared ───────────────────────────────────────────────────────
const CARD_FEATURES = [
  "Learn in a classroom with India's top faculties",
  "Printed study material, DPPs, and test series",
  "Peer-to-peer interaction and healthy competition",
];

// ── useClickOutside hook ──────────────────────────────────────────────────────
function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, [ref, handler]);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ScholarshipBanner() {
  return (
    <div className="scholarship-banner">
      <div className="sb-inner">
        <div className="sb-text">
          <div className="sb-title">
            <span className="sb-blue">IITian's Chinmay Academy</span> Scholarship Admission Test
          </div>
          <div className="sb-sub">Get up to 90% Scholarship*</div>
          <div className="sb-offer-row">
            <span className="sb-offer-star">✦</span>
            <span className="sb-offer-text">Special Offer</span>
          </div>
          <a href="#" className="sb-btn">
            {/* Register for <s style={{opacity:0.6,fontWeight:400}}>₹500</s> ₹99/- → */}
            Register<s style={{opacity:0.6,fontWeight:400}}></s> Now
          </a>
        </div>
        <div className="sb-img-wrap">
          <div className="sb-img-placeholder">🎓</div>
        </div>
      </div>
    </div>
  );
}

// ── Center Dropdown ───────────────────────────────────────────────────────────
function CenterDropdown({ city, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  const filtered = CITIES.filter(c => c.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="filter-chip-wrap" ref={ref}>
      <button
        className={`filter-chip${open ? " open" : ""}`}
        onClick={() => setOpen(o => !o)}
        type="button"
      >
        {/* Blue location pin icon */}
        <svg className="fc-icon fc-icon-blue" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
        </svg>
        <div className="fc-labels">
          <span className="fc-type">CENTER</span>
          <span className="fc-value">{city}</span>
        </div>
        <svg className={`fc-chevron${open ? " rotated" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 10l5 5 5-5z"/>
        </svg>
      </button>

      {open && (
        <div className="fc-dropdown">
          <div className="fc-search-wrap">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="#999">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            <input
              className="fc-search-input"
              placeholder="Search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="fc-list">
            {filtered.map(c => (
              <div
                key={c}
                className={`fc-item${c === city ? " active" : ""}`}
                onClick={() => { onChange(c); setOpen(false); setSearch(""); }}
              >
                {c}
              </div>
            ))}
            {filtered.length === 0 && <div className="fc-empty">No results</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Stream Dropdown ───────────────────────────────────────────────────────────
function StreamDropdown({ streamKey, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  const current = STREAMS.find(s => s.key === streamKey) || STREAMS[0];

  return (
    <div className="filter-chip-wrap" ref={ref}>
      <button
        className={`filter-chip stream-chip${open ? " open" : ""}`}
        onClick={() => setOpen(o => !o)}
        type="button"
      >
        {/* Stream/layers icon */}
        <svg className="fc-icon fc-icon-blue" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z"/>
        </svg>
        <div className="fc-labels">
          <span className="fc-type">STREAM</span>
          <span className="fc-value">{current.label}</span>
        </div>
        <svg className={`fc-chevron${open ? " rotated" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 10l5 5 5-5z"/>
        </svg>
      </button>

      {open && (
        <div className="fc-dropdown">
          <div className="fc-list">
            {STREAMS.map(s => (
              <div
                key={s.key}
                className={`fc-item${s.key === streamKey ? " active" : ""}`}
                onClick={() => { onChange(s.key); setOpen(false); }}
              >
                {s.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Session Pill Dropdown ─────────────────────────────────────────────────────
function SessionPill({ session, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  return (
    <div className="filter-chip-wrap" ref={ref} style={{ position: "relative" }}>
      <button
        className="session-pill"
        onClick={() => setOpen(o => !o)}
        type="button"
      >
        Session: {session}
        <svg className={`fc-chevron white${open ? " rotated" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 10l5 5 5-5z"/>
        </svg>
      </button>
      {open && (
        <div className="fc-dropdown small">
          {SESSIONS.map(s => (
            <div
              key={s}
              className={`fc-item${s === session ? " active" : ""}`}
              onClick={() => { onChange(s); setOpen(false); }}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Filters Modal ─────────────────────────────────────────────────────────────
function FiltersModal({ session, onSessionChange, selectedClass, onClassChange, onClose, courseCount }) {
  const [localSession, setLocalSession] = useState(session);
  const [localClass, setLocalClass] = useState(selectedClass);
  const ref = useRef(null);

  // Trap click outside to close
  useClickOutside(ref, onClose);

  const handleShow = () => {
    onSessionChange(localSession);
    onClassChange(localClass);
    onClose();
  };

  const handleClear = () => {
    setLocalSession("2026–27");
    setLocalClass("");
  };

  return (
    <div className="modal-overlay">
      <div className="modal-card" ref={ref}>
        <div className="modal-header">
          <span className="modal-title">Filters</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* Left tabs */}
          <div className="modal-tabs">
            <div className="modal-tab active">Session</div>
            <div className="modal-tab" onClick={() => {}}>Class</div>
          </div>
          {/* Right options */}
          <div className="modal-options">
            {SESSIONS.map(s => (
              <label key={s} className="modal-radio-row">
                <input
                  type="radio"
                  name="session"
                  value={s}
                  checked={localSession === s}
                  onChange={() => setLocalSession(s)}
                />
                <span>{s}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-clear" onClick={handleClear}>Clear Filters</button>
          <button className="modal-show" onClick={handleShow}>
            Show {courseCount} Programs
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Class Dropdown ─────────────────────────────────────────────────────────────
function ClassDropdown({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  return (
    <div className="filter-chip-wrap" ref={ref} style={{ position: "relative" }}>
      <button
        className={`cp2-filter-btn${selected ? " pill-active" : ""}`}
        onClick={() => setOpen(o => !o)}
        type="button"
      >
        {selected || "Class"}
        <svg className={`fc-chevron${open ? " rotated" : ""}`} width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 10l5 5 5-5z"/>
        </svg>
      </button>
      {open && (
        <div className="fc-dropdown small">
          <div
            className={`fc-item${!selected ? " active" : ""}`}
            onClick={() => { onChange(""); setOpen(false); }}
          >All Classes</div>
          {CLASSES.map(c => (
            <div
              key={c}
              className={`fc-item${c === selected ? " active" : ""}`}
              onClick={() => { onChange(c); setOpen(false); }}
            >
              {c}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CourseCard({ course }) {
  return (
    <div className="course-card">
      <span className={`course-card-tag ${course.tagClass}`}>{course.tag}</span>
      <div className="course-card-name">{course.name}</div>
      <div className="course-card-desc">{course.desc}</div>
      <ul className="course-card-features">
        {CARD_FEATURES.map((f, i) => (
          <li key={i}><span className="feature-check">✓</span>{f}</li>
        ))}
      </ul>
      <a href="#" className="course-card-link">Check course start dates and Fee →</a>
    </div>
  );
}

function SectionHeader({ num, title }) {
  return (
    <div className="cp2-sec-header">
      <span className="cp2-sec-num">{num}.0</span>
      <h2 className="cp2-sec-title">{title}</h2>
    </div>
  );
}

function FAQItem({ question }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item${open ? " open" : ""}`} onClick={() => setOpen(!open)}>
      <div className="faq-q">
        {question}
        <span className="faq-icon">{open ? "−" : "+"}</span>
      </div>
      {open && (
        <div className="faq-a">
          Our experienced faculty and structured curriculum ensure the best outcomes for every student. Please contact your nearest center or call us for detailed information about this topic.
        </div>
      )}
    </div>
  );
}

function LeadForm() {
  const WHATSAPP_NUMBER = "918849189845"; // ← replace with your WhatsApp number (country code + number, no +)

  const [form, setForm] = useState({
    name: "", mobile: "", cls: "", goal: "", mode: "", state: ""
  });
  const [terms, setTerms]     = useState(false);
  const [updates, setUpdates] = useState(false);
  const [errors, setErrors]   = useState({});

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim())              e.name   = "Name is required";
    if (!/^\d{10}$/.test(form.mobile))  e.mobile = "Enter valid 10-digit number";
    if (!form.cls)                       e.cls    = "Please choose a class";
    if (!form.goal)                      e.goal   = "Please choose a goal";
    if (!terms)                          e.terms  = "Please accept terms";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    const msg = [
      `*New Enquiry — IITian's Chinmay Academy*`,
      `👤 Name: ${form.name}`,
      `📱 Mobile: ${form.mobile}`,
      `🎓 Class: ${form.cls}`,
      `🎯 Goal: ${form.goal}`,
      `📚 Preferred Mode: ${form.mode || "Not specified"}`,
      `📍 State: ${form.state || "Not specified"}`,
      `📅 Session: 2026-27`,
    ].join("\n");

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  const field = (key) => errors[key]
    ? { border: "1.5px solid #e53e3e" }
    : {};

  return (
    <div className="lead-form">
      <div className="lf-title">Join IITian's Chinmay Academy!</div>
      <div className="lf-session">(Session 2026 – 27)</div>

      <div className="lf-field">
        <label>Name</label>
        <input placeholder="Enter name" value={form.name} onChange={set("name")} style={field("name")} />
        {errors.name && <span className="lf-err">{errors.name}</span>}
      </div>

      <div className="lf-field">
        <label>Mobile Number</label>
        <input placeholder="Enter 10 digit mobile number" value={form.mobile} onChange={set("mobile")} maxLength={10} style={field("mobile")} />
        {errors.mobile && <span className="lf-err">{errors.mobile}</span>}
      </div>

      <div className="lf-field">
        <label>Class</label>
        <div className="lf-select-wrap">
          <select value={form.cls} onChange={set("cls")} style={field("cls")}>
            <option value="">Choose class</option>
            <option>Class VIII</option><option>Class IX</option><option>Class X</option>
            <option>Class XI</option><option>Class XII</option><option>Dropper</option>
          </select>
          <svg className="lf-sel-arrow" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
        </div>
        {errors.cls && <span className="lf-err">{errors.cls}</span>}
      </div>

      <div className="lf-field">
        <label>Goal</label>
        <div className="lf-select-wrap">
          <select value={form.goal} onChange={set("goal")} style={field("goal")}>
            <option value="">Choose your goal</option>
            <option>JEE Main Advanced</option><option>JEE Main</option>
            <option>NEET</option><option>Foundation (Grade 6–10)</option>
          </select>
          <svg className="lf-sel-arrow" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
        </div>
        {errors.goal && <span className="lf-err">{errors.goal}</span>}
      </div>

      <div className="lf-field">
        <label>Preferred Programs</label>
        <div className="lf-select-wrap">
          <select value={form.mode} onChange={set("mode")}>
            <option value="">Preferred Mode</option>
            <option>Classroom</option><option>Online</option>
          </select>
          <svg className="lf-sel-arrow" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
        </div>
      </div>

      <div className="lf-field">
        <label>State</label>
        <div className="lf-select-wrap">
          <select value={form.state} onChange={set("state")}>
            <option value="">Choose State</option>
            <option>Gujarat</option><option>Rajasthan</option>
            <option>Maharashtra</option><option>Uttar Pradesh</option>
            <option>Delhi</option><option>Punjab</option><option>Madhya Pradesh</option>
          </select>
          <svg className="lf-sel-arrow" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z"/></svg>
        </div>
      </div>

      <label className="lf-check-row" style={errors.terms ? {color:"#e53e3e"} : {}}>
        <input type="checkbox" checked={terms} onChange={e => { setTerms(e.target.checked); setErrors(v => ({...v, terms: null})); }} />
        <span>I agree to IITian's Chinmay Academy's <a href="#" className="lf-link">Terms &amp; conditions</a></span>
      </label>
      {errors.terms && <span className="lf-err" style={{marginTop:-6}}>{errors.terms}</span>}

      <label className="lf-check-row" style={{marginBottom: 16}}>
        <input type="checkbox" checked={updates} onChange={e => setUpdates(e.target.checked)} />
        <span>I authorise the academy to send me updates via Phone calls, WhatsApp, SMS, Robocalls, Emails, or postal address.</span>
      </label>

      <button className="lf-submit" onClick={handleSubmit}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}>
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.528 5.855L0 24l6.293-1.506A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.891 0-3.665-.5-5.207-1.377l-.373-.22-3.736.894.938-3.627-.242-.386A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
        </svg>
        Submit via WhatsApp
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════
export default function CoursePage({ citySlug, streamSlug }) {
  const [city,       setCity]       = useState(fromSlug(citySlug  || "agra"));
  const [streamKey,  setStreamKey]  = useState(getStreamKey(streamSlug || "jee-main-advanced"));
  const [session,    setSession]    = useState("2026–27");
  const [selClass,   setSelClass]   = useState("");
  const [showFilter, setShowFilter] = useState(false);

  const d       = DATA[streamKey];
  const isNEET  = streamKey === "neet";
  const isGrade = streamKey === "grade";

  // Filter courses by selected class if any (simple demo filter)
  const visibleCourses = selClass
    ? d.courses.filter(c => c.desc.toLowerCase().includes(selClass.toLowerCase().replace("class ", "class")))
    : d.courses;

  return (
    <div className="cp2-root">

      {/* ── Scholarship Banner ── */}
      <ScholarshipBanner />

      {/* ── Courses For bar ── */}
      <div className="filter-bar">
        <span className="filter-label">Courses for:</span>
        <CenterDropdown city={city} onChange={setCity} />
        <StreamDropdown streamKey={streamKey} onChange={setStreamKey} />
      </div>

      {/* ── Page heading + filter pills ── */}
      <div className="cp2-heading-wrap">
        <h1 className="cp2-main-heading">{d.pageTitle(city)}</h1>
        <div className="cp2-filter-row">
          <button
            className="cp2-filter-btn"
            onClick={() => setShowFilter(true)}
            type="button"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}>
              <path d="M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39A.998.998 0 0019 4H5a1 1 0 00-.75 1.61z"/>
            </svg>
            Filters
          </button>
          <SessionPill session={session} onChange={setSession} />
          <ClassDropdown selected={selClass} onChange={setSelClass} />
        </div>
        <div className="cp2-stream-tag">{d.streamTag}</div>
      </div>

      {/* ── Course Cards ── */}
      <div className="cp2-cards-section">
        <div className={`cp2-cards-grid${(visibleCourses.length === 3 || d.courses.length === 3) ? " cols-3" : ""}`}>
          {visibleCourses.length > 0
            ? visibleCourses.map((c, i) => <CourseCard key={i} course={c} />)
            : d.courses.map((c, i) => <CourseCard key={i} course={c} />)
          }
        </div>
      </div>

      {/* ── White content sections ── */}
      <div className="cp2-content">

        {/* About */}
        <div className="cp2-section">
          <h2 className="cp2-sec-title" style={{ marginBottom: 14 }}>{d.about.heading(city)}</h2>
          {d.about.body(city).split("\n\n").map((p, i) => (
            <p key={i} className="cp2-body" dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") }} />
          ))}
        </div>

        {/* Section 1: Course table */}
        <div className="cp2-section">
          <SectionHeader num={1} title={d.sec1.title(city)} />
          {d.sec1.intro(city) && <p className="cp2-body">{d.sec1.intro(city)}</p>}
          <div className="cp2-table-wrap">
            <table className="cp2-table">
              <thead>
                <tr>
                  <th>Course Name</th>
                  <th>Who Can Apply?</th>
                  <th>Duration</th>
                  {d.sec1.tableWithFocus && <th>Primary Focus</th>}
                </tr>
              </thead>
              <tbody>
                {d.sec1.table.map((row, i) => (
                  <tr key={i}>
                    <td><strong>{row.course}</strong></td>
                    <td>{row.who}</td>
                    <td>{row.dur}</td>
                    {d.sec1.tableWithFocus && <td>{row.focus}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {d.sec1.showFee && (
            <div className="cp2-fee-box">
              <strong>Updated Fees:</strong>
              <ul>
                <li>Class VIII: ₹44,914 + Taxes</li>
                <li>Class IX: ₹50,848 + Taxes</li>
                <li>Class X: ₹52,542 + Taxes</li>
              </ul>
              <p>Each course includes printed study material, DPPs, test series, doubt-solving, and academic mentoring.</p>
            </div>
          )}
          {d.sec1.outro && <p className="cp2-body" style={{ marginTop: 14 }}>{d.sec1.outro}</p>}
        </div>

        {/* Section 2: Why */}
        <div className="cp2-section">
          <SectionHeader num={2} title={d.sec2.title(city)} />
          {d.sec2.intro(city) && <p className="cp2-body">{d.sec2.intro(city)}</p>}
          <div className="cp2-why-list">
            {d.sec2.points.map((pt, i) => (
              <div key={i} className="cp2-why-item">
                <span className="cp2-why-num">{i + 1}.</span>
                <div className="cp2-why-body">
                  <strong>{pt.h}</strong>
                  <p>{pt.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 3: Key features */}
        <div className="cp2-section">
          <SectionHeader num={3} title={typeof d.sec3.title === "function" ? d.sec3.title(city) : d.sec3.title} />
          <ul className="cp2-feat-list">
            {d.sec3.items.map((f, i) => (
              <li key={i}>
                <span className="cp2-feat-bullet">●</span>
                <span><strong>{f.h}</strong>{f.b ? ` — ${f.b}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Section 3b for grade only */}
        {isGrade && d.sec3b && (
          <div className="cp2-section">
            <SectionHeader num={4} title={d.sec3b.title} />
            <ul className="cp2-feat-list">
              {d.sec3b.items.map((f, i) => (
                <li key={i}>
                  <span className="cp2-feat-bullet">●</span>
                  <span><strong>{f.h}</strong>{f.b ? ` ${f.b}` : ""}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Section 4: Study Material */}
        <div className="cp2-section">
          <SectionHeader num={isGrade ? 5 : 4} title={d.sec4.title(city)} />
          <p className="cp2-body">{d.sec4.intro(city)}</p>
          {d.sec4.right.length > 0 ? (
            <div className="cp2-mat-grid">
              <div>
                {d.sec4.leftTitle && <div className="cp2-mat-col-title">{d.sec4.leftTitle}</div>}
                {d.sec4.left.map((m, i) => <a key={i} href="#" className="cp2-mat-link">{m}</a>)}
              </div>
              <div>
                {d.sec4.rightTitle && <div className="cp2-mat-col-title">{d.sec4.rightTitle}</div>}
                {d.sec4.right.map((m, i) => <a key={i} href="#" className="cp2-mat-link">{m}</a>)}
              </div>
            </div>
          ) : (
            <div className="cp2-mat-single">
              {d.sec4.left.map((m, i) => <a key={i} href="#" className="cp2-mat-link">{m}</a>)}
            </div>
          )}
        </div>

      </div>{/* end cp2-content */}

      {/* ── FAQs ── */}
      {!isGrade ? (
        <div className="cp2-faq-lead-wrap" style={{ marginTop: 20 }}>
          <div className="cp2-faq-section">
            <h2 className="cp2-faq-title">{d.faq.title(city)}</h2>
            <div className="cp2-faq-list">
              {d.faq.questions.map((q, i) => <FAQItem key={i} question={q} />)}
            </div>
          </div>
          <LeadForm />
        </div>
      ) : (
        <div style={{ maxWidth: 1200, margin: "20px auto 0", padding: "0 32px" }}>
          <div className="cp2-faq-section">
            <h2 className="cp2-faq-title">{d.faq.title(city)}</h2>
            <div className="cp2-faq-list">
              {d.faq.questions.map((q, i) => <FAQItem key={i} question={q} />)}
            </div>
          </div>
        </div>
      )}

      {/* ── Filters Modal ── */}
      {showFilter && (
        <FiltersModal
          session={session}
          onSessionChange={setSession}
          selectedClass={selClass}
          onClassChange={setSelClass}
          onClose={() => setShowFilter(false)}
          courseCount={d.courses.length}
        />
      )}

    </div>
  );
}